import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Store } from '@ngrx/store';
import { BehaviorSubject, fromEvent, Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { getCurrentDocument, getCurrentUser } from '@completion/selectors';
import { CommentStatus } from '@completion/enums';
import { Comment, ProcedureDocument, StatusChangeNote, User } from '@completion/models';
import { CommentStickyNoteOverlayService } from './comment-sticky-note-overlay.service';
import { MatDialog } from '@angular/material/dialog';
import { StatusChangeDialogComponent } from '../status-change-dialog/status-change-dialog.component';
import { DocumentUpdate } from '@completion/actions';
import { ConfirmationDialogComponent } from '../../../../shared/confirmation-dialog';

@Component({
  selector: 'app-comment-rail',
  templateUrl: './comment-sticky-note.component.html',
  styleUrls: ['./comment-sticky-note.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentStickyNoteComponent implements OnInit, OnChanges, OnDestroy {
  private static _nextInstance = 1;
  public instanceId: number;
  // Expose an observable for the template to async-pipe
  public comments$: BehaviorSubject<Comment[]> = new BehaviorSubject<Comment[]>([]);
  comments: Comment[] = [];
  @Input() nodeId?: number | string | null;
  @Input() reviewerMode?: boolean | null;
  @Input() comment!: Comment;
  @ViewChild('railTemplate', { static: true }) railTemplate?: TemplateRef<any>;

  private destroy$ = new Subject<void>();
  private overlayRef: OverlayRef | null = null;
  private portal: TemplatePortal<any> | null = null;
  private localScrollSubs: Subscription[] = [];
  private currentUser: User = null;

  // Ensure only one active sticky-note instance per comment id creates an overlay
  private static activeByCommentId: Map<number, CommentStickyNoteComponent> = new Map();

  constructor(
    private store: Store,
    private cdr: ChangeDetectorRef,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private overlayService: CommentStickyNoteOverlayService,
    private dialog: MatDialog,
  ) {
    this.instanceId = CommentStickyNoteComponent._nextInstance++;
  }

  private static registerInstance(commentId: number, instance: CommentStickyNoteComponent): boolean {
    const existing = CommentStickyNoteComponent.activeByCommentId.get(commentId);
    if (existing && existing !== instance) {
      try {
        console.warn(
          '[CommentStickyNote] Duplicate component instance for commentId',
          commentId,
          'existingInstanceId=',
          existing.instanceId,
          'newInstanceId=',
          instance.instanceId,
        );
      } catch {}
      return false;
    }
    CommentStickyNoteComponent.activeByCommentId.set(commentId, instance);
    return true;
  }

  private static unregisterInstance(commentId: number, instance: CommentStickyNoteComponent): void {
    const existing = CommentStickyNoteComponent.activeByCommentId.get(commentId);
    if (!existing || existing !== instance) {
      return;
    }
    CommentStickyNoteComponent.activeByCommentId.delete(commentId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.detachOverlay();

    // Unregister this comment id from the active registry
    const cid = this.comment?.id;
    if (cid != null) {
      CommentStickyNoteComponent.unregisterInstance(Number(cid), this);
    }
  }

  ngOnInit(): void {
    setTimeout(() => this.createOrUpdateOverlay());
    // Update overlay on scroll/resize
    try {
      fromEvent(window, 'scroll')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          try {
            this.overlayRef?.updatePosition();
          } catch {}
        });
      fromEvent(window, 'resize')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          try {
            this.overlayRef?.updatePosition();
          } catch {}
        });
    } catch {}

    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) this.currentUser = user;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.comment && !changes.comment.firstChange) {
      setTimeout(() => this.createOrUpdateOverlay());
    }
  }

  getCommentId(comment: any): string {
    if (!comment) return null;
    return String(comment.id) ?? null;
  }

  private createOrUpdateOverlay() {
    this.detachOverlay();
    if (!this.comment) return;
    const commentId = this.comment.id;
    if (!commentId) return;

    // Guard: only one instance per comment id should create an overlay
    const numericId = Number(commentId);
    if (!CommentStickyNoteComponent.registerInstance(numericId, this)) {
      return;
    }
    // Find highlight for this comment; if none, fall back to the parent node element
    let highlight: HTMLElement | null = null;
    // Prefer a unique .comment-highlight for this comment id
    const highlightMatches = Array.from(
      document.querySelectorAll<HTMLElement>(`.comment-highlight[data-comment-id="${String(commentId)}"]`),
    );
    if (highlightMatches.length === 1) {
      highlight = highlightMatches[0];
    } else if (highlightMatches.length > 1) {
      highlight = highlightMatches[0];
    }
    if (highlight) {
    }
    if (!highlight) {
      try {
        const nodeId = (this.comment as any)?.nodeId || (this.comment as any)?.documentNodeId || this.nodeId || null;
        if (nodeId != null) {
          // Try a generic data-node-id hook used on wrappers like section-node-wrapper / paragraph
          const nodeAnchors = Array.from(document.querySelectorAll<HTMLElement>(`[data-node-id="${String(nodeId)}"]`));
          if (nodeAnchors.length === 1) {
            highlight = nodeAnchors[0];
          } else if (nodeAnchors.length > 1) {
            highlight = nodeAnchors[0];
          }
          // Fallback: heading anchors with id="section-<nodeId>" or similar patterns
          if (!highlight) {
            const sectionAnchor =
              document.querySelector<HTMLElement>(`#section-${String(nodeId)}`) ||
              document.querySelector<HTMLElement>(`#node-${String(nodeId)}`);
            if (sectionAnchor) {
              highlight = sectionAnchor;
            }
          }
          if (highlight) {
            // If this comment has no selection text, visually highlight the parent node
            const hasSelection = !!this.getSelectionText(this.comment);
            if (!hasSelection) {
              try {
                highlight.classList.add('comment-node-has-sticky-note');
              } catch {}
            }
          }
        }
      } catch {}
    }
    if (!highlight) {
      try {
        console.warn(
          '[CommentStickyNote] No anchor element found for comment',
          commentId,
          'nodeId input=',
          this.nodeId,
          'comment.nodeId=',
          (this.comment as any)?.nodeId,
          'documentNodeId=',
          (this.comment as any)?.documentNodeId,
        );
      } catch {}
      return;
    }
    // Create overlay initially with zero horizontal offset. We'll compute and apply the
    // correct offset after the overlay is attached and the DOM has settled to avoid
    // reading stale/unfinished bounding rects (which causes centering on reload).
    const initialPositionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(highlight)
      .withPositions([{ originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: 0, offsetY: 0 }]);
    this.overlayRef = this.overlay.create({
      positionStrategy: initialPositionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass: 'comment-rail-overlay-panel',
    });
    if (this.railTemplate) {
      this.portal = new TemplatePortal(this.railTemplate, this.viewContainerRef, {
        $implicit: this.comment,
        comment: this.comment,
        component: this,
      });
      this.overlayRef.attach(this.portal);
    }
    // After the overlay is attached, wait a frame to ensure DOM is stable, then
    // compute the proper offsetX based on the highlight rect and update the
    // position strategy so the overlay is placed correctly.
    const applyFinalPosition = () => {
      try {
        // Retry loop guard: wait until highlight rect has sensible values and overlay panel has been rendered.
        // We'll attempt a few times to avoid computing offsetX too early (which caused centering on reload).
        let attempts = 0;
        const maxAttempts = 6;
        const tryCompute = () => {
          attempts++;
          try {
            const rect = highlight.getBoundingClientRect();
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const desiredRightMargin = 332; // px from right edge (32 + 300)
            const EXTRA_RIGHT_SHIFT = 50; // user requested fixed rightward shift in px

            // If rect values look invalid (e.g., right==0) and we still have attempts, retry next frame
            if ((typeof rect.right !== 'number' || rect.right === 0) && attempts < maxAttempts) {
              try {
                requestAnimationFrame(() => setTimeout(tryCompute, 0));
              } catch {
                setTimeout(tryCompute, 0);
              }
              return;
            }

            let offsetX = viewportWidth - rect.right - desiredRightMargin + EXTRA_RIGHT_SHIFT;

            // Clamp offsetX to avoid placing the rail completely outside the viewport.
            // Assume rails are ~220px wide; keep at least 16px margin from both sides.
            const MIN_MARGIN = 16;
            const MAX_RIGHT_SHIFT = viewportWidth - MIN_MARGIN;
            const MIN_RIGHT_SHIFT = -MAX_RIGHT_SHIFT;
            const clampedOffsetX = Math.max(MIN_RIGHT_SHIFT, Math.min(MAX_RIGHT_SHIFT, offsetX));
            if (clampedOffsetX !== offsetX) {
              offsetX = clampedOffsetX;
            }

            try {
              // Update the flexible position strategy in-place and then request a reposition
              (initialPositionStrategy as FlexibleConnectedPositionStrategy).withPositions([
                { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX, offsetY: 0 },
              ]);
              this.overlayRef?.updatePosition();
            } catch (e) {
              console.warn('[CommentStickyNote] Failed to update position strategy', e);
            }

            // Finally register the overlay rect with the overlay service for overlap detection
            const overlayPanel = this.getOverlayPanelElement();
            if (!overlayPanel && attempts < maxAttempts) {
              try {
                requestAnimationFrame(() => setTimeout(tryCompute, 0));
              } catch {
                setTimeout(tryCompute, 0);
              }
              return;
            }
            if (overlayPanel) {
              let overlayRect = overlayPanel.getBoundingClientRect();
              if ((overlayRect.width === 0 || overlayRect.height === 0) && attempts < maxAttempts) {
                try {
                  requestAnimationFrame(() => setTimeout(tryCompute, 0));
                } catch {
                  setTimeout(tryCompute, 0);
                }
                return;
              }
              if (overlayRect.width === 0 || overlayRect.height === 0) {
                overlayRect = {
                  ...overlayRect,
                  width: DEFAULT_OVERLAY_WIDTH,
                  height: DEFAULT_OVERLAY_HEIGHT,
                  right: overlayRect.left + DEFAULT_OVERLAY_WIDTH,
                  bottom: overlayRect.top + DEFAULT_OVERLAY_HEIGHT,
                } as DOMRect;
              }

              this.overlayService.register(String(commentId), overlayRect, this);
            }
          } catch (err) {
            if (attempts < maxAttempts) {
              try {
                requestAnimationFrame(() => setTimeout(tryCompute, 0));
              } catch {
                setTimeout(tryCompute, 0);
              }
            } else {
              console.warn('[CommentStickyNote] applyFinalPosition failed after retries', err);
            }
          }
        };
        // Kick off first attempt
        tryCompute();
      } catch (err) {
        console.warn('[CommentStickyNote] applyFinalPosition failed', err);
      }
    };

    // Use requestAnimationFrame for best chance DOM has finished layout; also fallback to setTimeout
    try {
      requestAnimationFrame(() => setTimeout(applyFinalPosition, 0));
    } catch {
      setTimeout(applyFinalPosition, 0);
    }
  }

  private detachOverlay() {
    if (this.overlayRef) {
      try {
        this.overlayRef.detach();
      } catch {}
      try {
        this.overlayRef.dispose();
      } catch {}
      this.overlayRef = null;
    }
    this.portal = null;
    // Unregister from overlay service
    if (this.comment) {
      const commentId = this.comment.id;
      if (commentId) this.overlayService.unregister(String(commentId));
    }
  }

  private getOverlayPanelElement(): HTMLElement | null {
    // Use overlayRef.overlayElement, which is the actual overlay panel
    if (!this.overlayRef) return null;
    return this.overlayRef.overlayElement;
  }

  // Hover/click logic for fan-out
  onMouseEnter() {
    if (!this.comment) return;

    if (CommentStickyNoteComponent.activeFanGroup && CommentStickyNoteComponent.activeFanGroup.length > 0) {
      return;
    }

    const commentId = this.comment.id;
    if (commentId == null) return;
    const overlapGroup = this.overlayService.getOverlapGroup(String(commentId));
    if (overlapGroup.length > 1) {
      // Print DOMRect values for debugging
      overlapGroup.forEach((o, _i) => {
        const rect = o.component.getOverlayPanelElement()?.getBoundingClientRect();
        if (rect) {
        }
      });
      // Add 'fanned' class to all overlays in the group
      overlapGroup.forEach((o, i) => {
        o.component.setFanClass(i);
      });
    }
  }

  onMouseLeave() {
    if (!this.comment) return;
    const commentId = this.comment.id;
    if (!commentId) return;
    // const overlapGroup = this.overlayService.getOverlapGroup(String(commentId));
    //overlapGroup.forEach(o => o.component.clearFanClass());
  }

  // onClickFan() {
  //   if (!this.comment) return;
  //   const commentId = this.comment.id;;
  //   if (!commentId) return;
  //   const overlapGroup = this.overlayService.getOverlapGroup(commentId);
  //   if (overlapGroup.length > 1) {
  //     // Fan out overlays to the left with a minimum of 225px
  //     overlapGroup.forEach((o, i) => {
  //       o.component.setFanClass(i, 'left');
  //     });
  //   }
  // }

  onClickFan() {
    if (!this.comment) return;
    const commentId = this.comment.id;
    if (!commentId) return;
    const overlapGroup = this.overlayService.getOverlapGroup(String(commentId));
    if (overlapGroup.length > 1) {
      // Fan out overlays to the left with a minimum of 225px
      overlapGroup.forEach((o, i) => {
        o.component.setFanClass(i, 'left');
      });

      // Attach timeout listeners for each member of the overlap group
      overlapGroup.forEach((o) => o.component.attachFanTimeoutListeners());

      // Mark the active group and start the shared timeout
      CommentStickyNoteComponent.activeFanGroup = overlapGroup.map((o) => o.component);
      CommentStickyNoteComponent.startFanOutTimeout();
    }
  }

  setFanClass(idx: number, mode: 'left' | 'right' = 'right') {
    const overlayPanel = this.getOverlayPanelElement();
    if (overlayPanel) {
      const rail = overlayPanel.querySelector('.comment-rail') as HTMLElement;
      if (rail) {
        rail.classList.add('fanned');
        rail.classList.add(`fan-${idx}`);
        let transform = '';
        if (mode === 'left') {
          // First stays put, second is -225px, third is -450px, etc.
          const left = idx === 0 ? 0 : -225 * idx;
          transform = `translateX(${left}px) translateY(${6 * idx}px) rotate(-1.5deg)`;
        } else {
          // Default: fan out to the right (hover)
          transform = `translateX(${16 * idx}px) translateY(${6 * idx}px) rotate(-1.5deg)`;
        }
        rail.style.transform = transform;
        rail.style.zIndex = `${1002 + idx}`;
      }
    }
  }

  // Restore clearFanClass so it actually resets the rail styles and classes
  clearFanClass() {
    const overlayPanel = this.getOverlayPanelElement();
    if (overlayPanel) {
      const rail = overlayPanel.querySelector('.comment-rail') as HTMLElement;
      if (rail) {
        rail.classList.remove('fanned');
        for (let i = 0; i < 10; i++) rail.classList.remove(`fan-${i}`);
        rail.style.transform = '';
        rail.style.zIndex = '';
      }
    }
  }

  // Status helpers for the status pill
  private normalizeStatus(c: any): string | null {
    if (!c) return null;
    const s = c.status ?? c.commentStatus ?? c.statusName ?? null;
    if (!s) return null;
    return String(s).toUpperCase();
  }

  getStatusLabel(c: any): string | null {
    const s = this.normalizeStatus(c);
    if (!s) return null;
    switch (s) {
      case CommentStatus.CREATED:
        return 'Created';
      case CommentStatus.IN_PROGRESS:
        return 'In progress';
      case CommentStatus.FIXED:
        return 'Fixed';
      case CommentStatus.DEFERRED:
        return 'Deferred';
      case CommentStatus.NOT_APPLICABLE:
        return 'N/A';
      case CommentStatus.PUSHED_BACK:
        return 'Pushed back';
      default:
        return s;
    }
  }

  getStatusIconComment(c: Comment): string {
    const s = this.normalizeStatus(c);
    return this.getStatusIcon(s);
  }

  getStatusIcon(status: string): string {
    if (!status) return 'info';
    switch (status) {
      case CommentStatus.CREATED:
        return 'report_problem';
      case CommentStatus.IN_PROGRESS:
        return 'autorenew';
      case CommentStatus.FIXED:
        return 'check_circle';
      case CommentStatus.DEFERRED:
        return 'pause_circle';
      case CommentStatus.NOT_APPLICABLE:
        return 'block';
      case CommentStatus.PUSHED_BACK:
        return 'arrow_back';
      default:
        return 'info';
    }
  }

  getStatusClass(c: any): string {
    const s = this.normalizeStatus(c);
    if (!s) return 'status-neutral';
    switch (s) {
      case CommentStatus.CREATED:
        return 'status-warning';
      case CommentStatus.IN_PROGRESS:
        return 'status-info';
      case CommentStatus.FIXED:
        return 'status-success';
      case CommentStatus.DEFERRED:
        return 'status-muted';
      case CommentStatus.NOT_APPLICABLE:
        return 'status-muted';
      case CommentStatus.PUSHED_BACK:
        return 'status-warning';
      default:
        return 'status-neutral';
    }
  }

  /** Map a raw status value (string/enum) to the status-* CSS class used by the UI */
  getStatusClassFromStatus(status: any): string {
    if (status == null) return 'status-neutral';
    const s = String(status).toUpperCase();
    switch (s) {
      case CommentStatus.CREATED:
        return 'status-warning';
      case CommentStatus.IN_PROGRESS:
        return 'status-info';
      case CommentStatus.FIXED:
        return 'status-success';
      case CommentStatus.DEFERRED:
        return 'status-muted';
      case CommentStatus.NOT_APPLICABLE:
        return 'status-muted';
      case CommentStatus.PUSHED_BACK:
        return 'status-warning';
      default:
        return 'status-neutral';
    }
  }

  // Map the comment status to a rail-level CSS class controlling the overall tint
  getRailTint(c: any): string {
    const s = this.normalizeStatus(c);
    if (!s) return '';
    switch (s) {
      case CommentStatus.PUSHED_BACK:
        return 'pushed-back';
      case CommentStatus.FIXED:
        // Treat fixed as accepted/green
        return 'accepted';
      case CommentStatus.IN_PROGRESS:
        return 'in-progress';
      case CommentStatus.DEFERRED:
      case CommentStatus.NOT_APPLICABLE:
        return 'deferred';
      case CommentStatus.CREATED:
        return 'created';
      default:
        return '';
    }
  }

  // Whether the current user is allowed to accept this comment's resolution.
  // Show the button while the comment is not yet accepted, the current user
  // is the author, and the status is in a resolvable state.
  canAccept(c: Comment): boolean {
    if (!c) {
      return false;
    }
    const status = this.normalizeStatus(c);
    const isAuthor = c.author?.id === this.currentUser?.id;
    const isFinalStatus = status === CommentStatus.FIXED || status === CommentStatus.DEFERRED || status === CommentStatus.NOT_APPLICABLE;
    return !c.accepted && isAuthor && isFinalStatus;
  }

  onAcceptClick(_event: any, c: Comment) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Accept comment resolution?',
        message: 'Are you sure you want to accept this comment resolution?',
        confirmText: 'Accept',
        cancelText: 'Cancel',
        isCancelDisplay: true,
        confirmationTile: 'Accept',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const commentId = c?.id;
        if (!commentId) {
          return;
        }

        // Persist the accepted flag back to the backend by updating the
        // corresponding comment inside the current document and dispatching
        // a DocumentUpdate. Avoid mutating potentially frozen objects by
        // working on a cloned document/comment only.
        this.store
          .select(getCurrentDocument)
          .pipe(take(1))
          .subscribe((doc: ProcedureDocument | null | undefined) => {
            if (!doc) {
              return;
            }

            const cloned = JSON.parse(JSON.stringify(doc)) as ProcedureDocument;
            const target = this.findCommentInNodes(cloned.nodes || [], Number(commentId));
            if (!target) {
              return;
            }

            target.accepted = true;

            try {
              this.store.dispatch(new DocumentUpdate(cloned));
              try {
                this.cdr.markForCheck();
              } catch {}
            } catch {
              // Swallow dispatch errors to avoid breaking the UI; logging can be added if desired
            }
          });
      });
  }

  getSelectionText(c: any): string {
    if (!c) return '';
    return (c.selectionText ?? c.selection ?? '') as string;
  }

  getAuthorLabel(c: any): string {
    if (!c) return 'Unknown';
    return c.authorName ?? c.author?.name ?? c.author?.displayName ?? c.createdBy ?? 'Unknown';
  }

  getCreatedAt(c: any): Date | null {
    if (!c) return null;
    const v = c.createdAt ?? c.created ?? c.timestamp ?? c.date;
    if (!v) return null;
    const d = v instanceof Date ? v : new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  // (trackBy helper removed — template uses getCommentId directly)

  onNoteHover(c: any, isEnter: boolean) {
    try {
      const id = c.id;
      const idx = (c as any)?.commentIndex;
      const selectors: string[] = [];
      if (id != null) selectors.push(`.comment-highlight[data-comment-id="${id}"]`);
      if (idx != null) selectors.push(`.comment-highlight[data-comment-index="${idx}"]`);
      const snippet = this.getSelectionText(c) || '';

      let elements: HTMLElement[] = [];
      for (const s of selectors) {
        try {
          const found = Array.from(document.querySelectorAll<HTMLElement>(s));
          if (found.length) elements = elements.concat(found);
        } catch {}
      }

      if (!elements.length && snippet) {
        const all = Array.from(document.querySelectorAll<HTMLElement>('.comment-highlight'));
        for (const el of all) {
          try {
            const txt = (el.textContent || '').trim();
            if (txt && snippet && txt.indexOf(String(snippet)) >= 0) {
              elements.push(el);
            }
          } catch {
            /* ignore per-element failures */
          }
        }
      }

      if (!elements.length) {
      }

      for (const el of elements) {
        try {
          if (isEnter) {
            el.classList.add('highlight-focused');
            el.setAttribute('data-highlight-target', 'true');
          } else {
            el.classList.remove('highlight-focused');
            el.removeAttribute('data-highlight-target');
          }
        } catch {}
      }
    } catch {}
  }

  // Overlay management
  private createOverlayForElement(target: HTMLElement) {
    // detach existing overlay if present
    try {
      // If overlay exists, just update its position target
      if (this.overlayRef) {
        try {
          (this.overlayRef.getConfig().positionStrategy as FlexibleConnectedPositionStrategy).setOrigin(target);
        } catch {}
        try {
          this.overlayRef.updatePosition();
        } catch {}
        return;
      }
      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(target)
        .withPositions([
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top',
            offsetX: 12,
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'top',
            offsetX: -12,
          },
        ] as ConnectedPosition[]);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      });

      if (this.railTemplate) {
        // Attach the template portal without a fixed context — the template will use comments$ via async pipe
        this.portal = new TemplatePortal(this.railTemplate, this.viewContainerRef, { component: this });
        this.overlayRef.attach(this.portal);
      }
    } catch {
      this.detachOverlay();
    }
  }
  // language: typescript
  // Add these members and methods inside the CommentRailComponent class

  // Shared timeout + active group
  private static fanOutTimeout: any = null;
  private static FANOUT_TIMEOUT_MS = 1000;
  private static activeFanGroup: CommentStickyNoteComponent[] = [];

  // Start a single shared timeout that will clear the entire active fan group
  private static startFanOutTimeout() {
    CommentStickyNoteComponent.clearFanOutTimeout();
    CommentStickyNoteComponent.fanOutTimeout = setTimeout(() => {
      try {
        CommentStickyNoteComponent.activeFanGroup.forEach((comp) => {
          try {
            comp.clearFanClass();
          } catch {}
        });
        CommentStickyNoteComponent.activeFanGroup.forEach((comp) => {
          try {
            comp.detachFanTimeoutListeners();
          } catch {}
        });
      } finally {
        CommentStickyNoteComponent.activeFanGroup = [];
        CommentStickyNoteComponent.fanOutTimeout = null;
      }
    }, CommentStickyNoteComponent.FANOUT_TIMEOUT_MS);
  }

  private static clearFanOutTimeout() {
    if (CommentStickyNoteComponent.fanOutTimeout) {
      clearTimeout(CommentStickyNoteComponent.fanOutTimeout);
      CommentStickyNoteComponent.fanOutTimeout = null;
    }
  }

  // Attach per-overlay mouse/pointer listeners that reference the shared timeout
  private static readonly FAN_LEAVE_DEBOUNCE_MS = 500; // increased debounce to avoid accidental collapse
  private attachFanTimeoutListeners() {
    const overlayPanel = this.getOverlayPanelElement();
    if (!overlayPanel) return;
    const rail = overlayPanel.querySelector('.comment-rail') as HTMLElement | null;
    if (!rail) return;
    // Use pointer events which behave better across transformed elements
    rail.removeEventListener('pointerenter', this._fanMouseEnterTimeout as EventListener);
    rail.removeEventListener('pointerleave', this._fanMouseLeaveTimeout as EventListener);
    rail.addEventListener('pointerenter', this._fanMouseEnterTimeout as EventListener);
    rail.addEventListener('pointerleave', this._fanMouseLeaveTimeout as EventListener);
  }

  private detachFanTimeoutListeners() {
    const overlayPanel = this.getOverlayPanelElement();
    if (!overlayPanel) return;
    const rail = overlayPanel.querySelector('.comment-rail') as HTMLElement | null;
    if (!rail) return;
    rail.removeEventListener('pointerenter', this._fanMouseEnterTimeout as EventListener);
    rail.removeEventListener('pointerleave', this._fanMouseLeaveTimeout as EventListener);
  }

  private _fanMouseEnterTimeout = () => {
    // clear shared timeout so the fan remains open while hovering
    CommentStickyNoteComponent.clearFanOutTimeout();
  };

  private _fanMouseLeaveTimeout = () => {
    // Debounce a bit to avoid immediate collapse when moving between overlapped rails
    setTimeout(() => {
      // If no rails in the active group are hovered, restart the shared timeout
      if (!CommentStickyNoteComponent.activeFanGroup.some((comp) => comp.isMouseOverFan())) {
        CommentStickyNoteComponent.startFanOutTimeout();
      }
    }, CommentStickyNoteComponent.FAN_LEAVE_DEBOUNCE_MS);
  };

  private isMouseOverFan(): boolean {
    const overlayPanel = this.getOverlayPanelElement();
    if (!overlayPanel) return false;
    const rail = overlayPanel.querySelector('.comment-rail') as HTMLElement | null;
    if (!rail) return false;
    return rail.matches(':hover');
  }

  // Replace or update onClickFan to attach listeners and start the timeout

  // Update ngOnDestroy to remove this instance from active group and clean listeners/timeouts when needed

  // Add method to open status change dialog and persist the change
  onStatusPillClick(event: MouseEvent, comment: any) {
    try {
      event.stopPropagation();
      event.preventDefault();
    } catch {}
    // Do not allow status changes on an already-accepted comment
    if (comment && comment.accepted) {
      try {
        console.debug('[CommentStickyNote] Status change blocked: comment is already accepted', comment?.id);
      } catch {}
      return;
    }
    if (!comment) return;
    const commentId = comment.id;
    if (!commentId) return;

    const dlg = this.dialog.open(StatusChangeDialogComponent, {
      width: '560px',
      data: { commentId: Number(commentId), currentStatus: comment.status },
    });

    dlg
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: Partial<StatusChangeNote> | null) => {
        if (!result) return;
        this.store
          .select(getCurrentDocument)
          .pipe(take(1))
          .subscribe((doc: ProcedureDocument | null | undefined) => {
            if (!doc) return;
            const cloned = JSON.parse(JSON.stringify(doc)) as ProcedureDocument;
            const cid = Number(commentId);
            const changed = this.findAndUpdateCommentInNodes(cloned.nodes || [], cid, result as StatusChangeNote);
            if (changed) {
              // Strip any temporary negative ids from statusChangeNotes before sending to backend
              this.stripTemporaryStatusChangeIds(cloned.nodes);
              // Dispatch the class-style DocumentUpdate action so effects persist the modified document
              try {
                this.store.dispatch(new DocumentUpdate(cloned));
                // mark for check in case OnPush components need to update
                try {
                  this.cdr.markForCheck();
                } catch {}
              } catch {
                // Fallback: try functional action creator if present
                try {
                  this.store.dispatch(new DocumentUpdate(cloned));
                } catch (err) {
                  console.warn('[CommentRail] Failed to dispatch DocumentUpdate action', err);
                }
              }
            }
          });
      });
  }

  private findAndUpdateCommentInNodes(nodes: any[] | undefined, commentId: number, note: Partial<StatusChangeNote>): boolean {
    if (!Array.isArray(nodes)) return false;
    for (const n of nodes) {
      if (!n) continue;
      const comments = Array.isArray(n.comments) ? n.comments : [];
      for (const c of comments) {
        if (!c) continue;
        const cid = c.id ?? c.commentId;
        if (cid == null) continue;
        if (Number(cid) === Number(commentId)) {
          const prev = c.status;
          c.status = note.changeTo ?? c.status;
          const entry: any = {
            // If caller provided an id, use it; otherwise leave null so backend assigns a real id
            id: note && note.id != null ? Number((note as any).id) : null,
            // Ensure commentId is always a number
            commentId: Number(commentId),
            // Attach changedBy: prefer provided value, otherwise use current logged-in user
            changedBy: note && note.changedBy ? note.changedBy : this.currentUser,
            changeNote: note.changeNote || '',
            changeDate: note.changeDate ? note.changeDate : new Date().toISOString(),
            changeFrom: note.changeFrom ?? prev,
            changeTo: note.changeTo,
          };
          if (!Array.isArray(c.statusChangeNotes)) c.statusChangeNotes = [];
          c.statusChangeNotes.push(entry);
          // Sort descending by changeDate (newest first)
          c.statusChangeNotes.sort((a, b) => new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime());
          return true;
        }
      }
      if (Array.isArray(n.children) && n.children.length) {
        const found = this.findAndUpdateCommentInNodes(n.children, commentId, note);
        if (found) return true;
      }
    }
    return false;
  }

  private findCommentInNodes(nodes: any[] | undefined, commentId: number): Comment | null {
    if (!Array.isArray(nodes)) return null;
    for (const n of nodes) {
      if (!n) continue;
      const comments = Array.isArray(n.comments) ? n.comments : [];
      for (const c of comments) {
        if (!c) continue;
        const cid = c.id ?? c.commentId;
        if (cid == null) continue;
        if (Number(cid) === Number(commentId)) {
          return c;
        }
      }
      if (Array.isArray(n.children) && n.children.length) {
        const found = this.findCommentInNodes(n.children, commentId);
        if (found) return found;
      }
    }
    return null;
  }

  // Helpers for status-change note display + tooltip
  getUserInitials(user: any): string {
    if (!user) return '';
    const name = (user.name ?? user.displayName ?? user.fullName ?? user.email ?? '').toString().trim();
    if (!name) return '';
    const parts = name.split(/\s+/).filter((p) => p.length);
    if (!parts.length) return '';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  previewNote(text: any, max = 60): string {
    if (text == null) return '';
    const s = String(text).replace(/\s+/g, ' ').trim();
    if (s.length <= max) return s;
    return s.slice(0, max).trim() + '…';
  }

  formatDate(val: any): string {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return d.toISOString();
    }
  }

  /** Format only the date portion (no time) for compact history display */
  formatDateDateOnly(val: any): string {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString();
    } catch {
      return d.toISOString().split('T')[0];
    }
  }

  getStatusChangeTooltip(note: any): string {
    if (!note) return '';
    const from = this.getStatusLabel(note.changeFrom) || '';
    const to = this.getStatusLabel(note.changeTo) || '';
    const who = note.changedBy ? (note.changedBy.name ?? note.changedBy.displayName ?? note.changedBy.email ?? 'Unknown') : 'Unknown';
    const initials = this.getUserInitials(note.changedBy) || '';
    const when = this.formatDate(note.changeDate) || '';
    const noteText = note.changeNote ? String(note.changeNote).trim() : '';
    const lines: string[] = [];
    lines.push(`${from} → ${to}`);
    lines.push(`By: ${who} ${initials ? `(${initials})` : ''}`);
    if (when) lines.push(`At: ${when}`);
    if (noteText) lines.push('', noteText);
    return lines.join('\n');
  }

  /**
   * Walk all nodes and normalize any statusChangeNotes entries with a negative id
   * (temporary client ids) before sending to the backend — don't drop the note,
   * instead set id to null so the backend will assign a permanent id.
   */
  private stripTemporaryStatusChangeIds(nodes: any[] | undefined): void {
    if (!Array.isArray(nodes)) {
      return;
    }
    for (const n of nodes) {
      if (!n) {
        continue;
      }
      if (Array.isArray(n.comments)) {
        for (const c of n.comments) {
          if (c && Array.isArray(c.statusChangeNotes)) {
            for (const note of c.statusChangeNotes) {
              if (!note) continue;
              const id = note && note.id != null ? Number(note.id) : null;
              if (id != null && id < 0) {
                // convert temporary negative id to null so backend will create a new id
                note.id = null;
              }
            }
          }
        }
      }
      if (Array.isArray(n.children) && n.children.length) {
        this.stripTemporaryStatusChangeIds(n.children);
      }
    }
  }
}

const DEFAULT_OVERLAY_WIDTH = 220; // px, match .comment-rail width in SCSS
const DEFAULT_OVERLAY_HEIGHT = 240; // px, match min-height in SCSS
